import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    RichTextField,
    ReferenceField,
    EditButton,
    Labeled,
    Pagination,
    Datagrid,
    ReferenceManyField,
    useRecordContext,
    Create,
    SimpleForm,
    required,
    useRefresh,
    useCreate,
    useUpdate,
    useNotify,
    TextInput,
    Edit,
    NullableBooleanInput,
    SelectInput,
    useDataProvider,
} from 'react-admin';
import FullNameField from '../../common/FullNameField';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import * as React from 'react';
import { SoftDeleteWithDialogAction } from '../../common/SoftDeleteWithDialogAction';
import {
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Paper,
    TablePagination,
} from '@mui/material';
import { RESOLUTION_STATUS } from '../../common/constants';
import { JsonToTagsView } from './IssuesList';
import { useQuery } from 'react-query';
import { useState, useEffect } from 'react';
import { RemoveCircle } from '@mui/icons-material';
import { goApi } from '../../services';

const Comments = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total] = useState(0);

    const fetchComments = async () => {
        if (!record?.id) return;

        setLoading(true);
        try {
            const d = await goApi.get(`/issues/${record.id}/comments`);
            setComments(d?.data?.items || []);
        } catch (err) {
            notify('Error fetching comments', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchComments();
    }, [record?.id]);

    const postSave = async (data: any) => {
        try {
            await goApi.post(`/issues/${record.id}/comments`, {
                comment: data.comment,
                resolutionStatus: data.resolutionStatus,
            });

            notify('Comment added', { type: 'success' });
            fetchComments();

            const form = document.getElementById('comment-form') as HTMLFormElement;
            if (form) form.reset();
        } catch (err) {
            notify('Error adding comment', { type: 'error' });
        }
    };

    const deleteComment = async (id: string) => {
        try {
            await goApi.delete(`/issues/comments/${id}`);
            notify('Comment deleted', { type: 'success' });
            fetchComments();
        } catch (err) {
            notify('Error deleting comment', { type: 'error' });
        }
    };
    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    return (
        <div className={'w-full'}>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell>Comment</TableCell>
                            <TableCell>Resolution status</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4}>Loading...</TableCell>
                            </TableRow>
                        ) : comments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4}>No comments yet</TableCell>
                            </TableRow>
                        ) : (
                            comments.map((comment: any) => (
                                <TableRow key={comment.id}>
                                    <TableCell padding="checkbox">
                                        <input type="checkbox" />
                                    </TableCell>
                                    <TableCell>{comment.comment}</TableCell>
                                    <TableCell>{comment.resolutionStatus}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => deleteComment(comment.id)}>
                                            <RemoveCircle />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={total || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
            />
            <Create redirect={undefined}>
                <SimpleForm
                    defaultValues={{
                        resolution_status: record.resolution_status,
                    }}
                    id={'comment-form'}
                    onSubmit={postSave}
                >
                    <TextInput source={'comment'} fullWidth multiline />
                    <SelectInput
                        source="resolution_status"
                        label={'Resolution Status'}
                        fullWidth
                        choices={RESOLUTION_STATUS}
                    />
                </SimpleForm>
            </Create>
        </div>
    );
};
const ExtraDataSection = () => {
    const record = useRecordContext();

    if (!record?.extraData) return null;

    const formatKey = (key: string) =>
        key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

    return (
        <div>
            {Object.entries(record.extraData).map(([key, value]) => (
                <div
                    key={key}
                    style={{
                        color: 'rgba(0, 0, 0, 0.87)',
                        fontSize: '0.875rem',
                        lineHeight: 1.43,
                        marginBottom: '1px',
                    }}
                >
                    {formatKey(key)}: {String(value)}
                </div>
            ))}
        </div>
    );
};
const UserDetails = () => {
    const record = useRecordContext();
    const user = record?.user;

    if (!user) return null;
    return (
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '6px' }}>
            <div
                style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: '#c4c4c4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: 500,
                    color: '#ffffff',
                }}
            >
                {user.firstName?.charAt(0)?.toUpperCase()}
            </div>
            <div style={{ marginLeft: '10px' }}>
                <div
                    style={{
                        fontSize: '0.95rem',
                        color: '#3f51b5',
                        textTransform: 'lowercase',
                        cursor: 'pointer',
                    }}
                >
                    {user.firstName} {user.lastName}
                </div>

                <div
                    style={{
                        fontSize: '0.95rem',
                        marginTop: '2px',
                        color: '#3f51b5',
                    }}
                >
                    {user.phone}
                </div>
            </div>
        </div>
    );
};

const IssueTopicDetails = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const issueTopicId = record?.issueTopicId;

    const { data, isLoading, error } = useQuery(
        ['issue-topic', issueTopicId],
        () => dataProvider.getOne('issue-topics', { id: issueTopicId }),
        { enabled: !!issueTopicId },
    );

    if (!issueTopicId) return null;
    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error</div>;

    return (
        <div
            style={{
                fontSize: '0.875rem',
                marginTop: '8px',
                color: 'rgba(63, 81, 181, 1)',
                fontWeight: 400,
                cursor: 'pointer',
            }}
        >
            {data?.data?.name}
        </div>
    );
};

export const IssuesShow = () => {
    return (
        <Show>
            <SimpleShowLayout>
                <TextField source="issueDescription" />
                <Labeled label="Extra data">
                    <ExtraDataSection />
                </Labeled>
                <JsonToTagsView source={'deviceMeta'} />
                <Labeled label="Issue topic">
                    <IssueTopicDetails />
                </Labeled>
                <Labeled label="Student">
                    <UserDetails />
                </Labeled>
                <TextField source="resolutionStatus" />
                <FormattedDateTimeField source="createdAt" />
                <Divider />
                <Labeled fullWidth label={'Comments'}>
                    <Comments />
                </Labeled>
            </SimpleShowLayout>
        </Show>
    );
};
